import {
    createContext,
    useContext,
    useRef,
    useState,
    ReactNode,
} from 'react'

export type AlertType =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'

export interface AlertData {
    id: number
    title?: string
    message: string
    type: AlertType
    duration: number
    closable: boolean
}

export interface ShowAlertOptions {
    title?: string
    message: string
    type?: AlertType
    duration?: number
    closable?: boolean
}

interface AlertContextType {
    alerts: AlertData[]
    showAlert: (options: ShowAlertOptions) => void
    hideAlert: (id: number) => void
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export function AlertProvider({ children }: { children: ReactNode }) {
    const [alerts, setAlerts] = useState<AlertData[]>([])
    const queueCountRef = useRef(0)

    const hideAlert = (id: number) => {
        setAlerts((prev) => prev.filter((a) => a.id !== id))
    }

    const showAlert = ({
        title,
        message,
        type = 'primary',
        duration = 3000,
        closable = true,
    }: ShowAlertOptions) => {
        const id = Date.now() + Math.random()
        const delay = queueCountRef.current * 100
        queueCountRef.current += 1

        window.setTimeout(() => {
            setAlerts((prev) => [...prev, { id, title, message, type, duration, closable }])
            window.setTimeout(() => {
                hideAlert(id)
                queueCountRef.current = Math.max(0, queueCountRef.current - 1)
            }, duration)
        }, delay)
    }

    return (
        <AlertContext.Provider value={{ alerts, showAlert, hideAlert }}>
            {children}
        </AlertContext.Provider>
    )
}

export function useAlert() {
    const context = useContext(AlertContext)

    if (!context) {
        throw new Error('useAlert deve ser utilizado dentro do AlertProvider')
    }

    return context
}
